import { CSSProperties, MouseEventHandler, ReactNode, RefObject, useState } from 'react';
import { useToggle } from 'react-use';
import styled from 'styled-components';

import { useClickOutside } from '@/shared/hooks/useClickOutside';
import { BaseProps } from '@/shared/types';
import { Icon } from '@/shared/uikit/Icon';
import { BtnProps, Button } from '@/shared/uikitV2/Button';

export type DropdownButtonRenderListProps<T> = (
  onSelect: (category: T) => void,
  listRef: RefObject<HTMLDivElement>,
  selectStyles?: CSSProperties,
  filter?: T[],
) => ReactNode;

type DropdownButtonProps<T> = {
  onSelect: (category: T) => void;
  btnVariant?: BtnProps['type'];
  listPosH?: 'center' | 'right';
  listPosV?: 'top' | 'bottom';
  btnStyle?: CSSProperties;
  listStyle?: CSSProperties;
  renderList: DropdownButtonRenderListProps<T>;
};

export function DropdownButton<T>({
  onSelect,
  btnVariant,
  listPosH = 'right',
  listPosV = 'bottom',
  btnStyle,
  listStyle,
  children,
  renderList,
  ...props
}: BaseProps & DropdownButtonProps<T>) {
  const [show, toggleShow] = useToggle(false);
  const { ref: listRef } = useClickOutside({ show, onHide: toggleShow });
  const [selectStyles, setSelectStyles] = useState<CSSProperties>();

  const showList = () => {
    const styles = Object.assign({}, listStyle ?? {}, {
      position: 'absolute',
      zIndex: 5,
      width: props.style?.width,
    });

    if (listPosV === 'bottom') {
      styles.top = 'calc(100% + 4px)';
    } else {
      styles.bottom = 48; // default button size +4
    }

    if (listPosH === 'right') {
      styles.right = 0;
    } else {
      styles.left = '50%';
      styles.transform = 'translate(-50%)';
    }

    setSelectStyles(styles);
    toggleShow(true);
  };

  const hideList = () => {
    setSelectStyles(undefined);
    toggleShow(false);
  };

  const onClickBtn: MouseEventHandler = (ev) => {
    ev.stopPropagation();

    if (show) {
      hideList();
    } else {
      showList();
    }
  };

  const handleSelect = (category: T) => {
    hideList();
    onSelect(category);
  };

  return (
    <BtnContainer {...props}>
      <Button
        className="add-button"
        style={btnStyle}
        size="middle"
        type={btnVariant}
        onClick={onClickBtn}
      >
        {children}
        <Icon name="caret-down" size="s20" />
      </Button>
      {show && renderList(handleSelect, listRef, selectStyles)}
    </BtnContainer>
  );
}

const BtnContainer = styled.div`
  position: relative;

  .add-button {
    width: 100%;
  }
`;
